import { selectPrimaryStatsDisplay, type BattingStatsLike, type PitchingStatsLike } from '@/domain/playerStatsDisplay'
import styles from './PlayerStatus.module.css'

// `lib/npbApi.ts` の `PlayerSeasonData` は features/ から直接 import できない
// （SPEC §7.4 / CLAUDE.md 禁止事項5: features は lib に直接依存しない）ため、
// このコンポーネントが必要とする形だけをここで定義する。呼び出し側（app/）が
// `lib/` の型を構造的に満たす値を渡す。
interface PlayerProfileLike {
  readonly name: string
  readonly team: string | null
  readonly position: string | null
}

interface PlayerSeasonDataLike {
  readonly profile: PlayerProfileLike
  readonly latestBattingStats: BattingStatsLike | null
  readonly latestPitchingStats: PitchingStatsLike | null
  readonly fetchedAt: string
}

interface ApiErrorLike {
  readonly message: string
}

interface Props {
  data: PlayerSeasonDataLike | null
  loading: boolean
  error: ApiErrorLike | null
}

export function PlayerStatus({ data, loading, error }: Props) {
  if (loading) {
    return (
      <section className={styles.container} aria-label="選手情報">
        <p role="status" className={styles.loading}>成績を読み込み中です…</p>
      </section>
    )
  }

  if (error || !data) {
    return (
      <section className={styles.container} aria-label="選手情報">
        <p role="alert" className={styles.error}>
          {error?.message ?? '成績を取得できませんでした。'}
        </p>
      </section>
    )
  }

  const primary = selectPrimaryStatsDisplay(data.profile.position, data.latestBattingStats, data.latestPitchingStats)

  return (
    <>
      <section className={styles.container} aria-label="出場状況">
        <div className={styles.header}>
          <h2 className={styles.playerName}>{data.profile.name}</h2>
          {data.profile.team && <span className={styles.team}>{data.profile.team}</span>}
        </div>
        {/* F-03「直近の試合出場状況」の実データ化は Phase 2（ADR-0007）。
            フェイクの出場状況データを実データの成績と混在させて表示しないため、
            準備中であることを明示するメッセージに置き換えている（Issue #9）。 */}
        <p className={styles.preparing}>出場状況の表示は準備中です</p>
      </section>

      <section className={styles.statsCard} aria-label="成績">
        <h3 className={styles.statsHeading}>
          {primary.kind === 'none' ? '成績' : `${primary.stats.year}年シーズン成績（${primary.stats.team}）`}
        </h3>

        {primary.kind === 'none' && <p className={styles.empty}>成績データがまだありません</p>}

        {primary.kind === 'batting' && (
          <dl className={styles.meta}>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>試合</dt>
              <dd className={styles.metaValue}>{primary.stats.games}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>打率</dt>
              <dd className={styles.metaValue}>{primary.stats.battingAverage}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>安打</dt>
              <dd className={styles.metaValue}>{primary.stats.hits}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>本塁打</dt>
              <dd className={styles.metaValue}>{primary.stats.homeRuns}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>打点</dt>
              <dd className={styles.metaValue}>{primary.stats.runsBattedIn}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>盗塁</dt>
              <dd className={styles.metaValue}>{primary.stats.stolenBases}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>出塁率</dt>
              <dd className={styles.metaValue}>{primary.stats.onBasePercentage}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>長打率</dt>
              <dd className={styles.metaValue}>{primary.stats.sluggingPercentage}</dd>
            </div>
          </dl>
        )}

        {primary.kind === 'pitching' && (
          <dl className={styles.meta}>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>登板</dt>
              <dd className={styles.metaValue}>{primary.stats.appearances}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>勝敗</dt>
              <dd className={styles.metaValue}>{primary.stats.wins}勝{primary.stats.losses}敗</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>セーブ</dt>
              <dd className={styles.metaValue}>{primary.stats.saves}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>ホールド</dt>
              <dd className={styles.metaValue}>{primary.stats.holds}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>防御率</dt>
              <dd className={styles.metaValue}>{primary.stats.earnedRunAverage}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>投球回</dt>
              <dd className={styles.metaValue}>{primary.stats.inningsPitched}</dd>
            </div>
            <div className={styles.metaRow}>
              <dt className={styles.metaLabel}>奪三振</dt>
              <dd className={styles.metaValue}>{primary.stats.strikeouts}</dd>
            </div>
          </dl>
        )}

        <p className={styles.updatedAt}>
          更新: {new Date(data.fetchedAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' })}
        </p>
      </section>
    </>
  )
}
